import {
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  PageContainer,
  RuntimeDiagnosticBanner,
  type ComponentDiagnostic,
} from "@swot/base-components";
import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
  type CmtsGridRowAction,
} from "@components/data-grid/index.js";
import type { CmtsBackendEndpoint } from "@cmts/api/cmtsBackendEndpointRegistry.js";

type Row = Record<string, unknown>;
type ExportFormat = "CSV" | "XLS" | "JSON";
type DetailMode = "schema" | "fallback";

type ProbeResult = {
  readonly success: boolean;
  readonly title: string;
  readonly message: string;
};

export type CmtsBackendEndpointCoveragePageProps = {
  readonly endpoints?: readonly CmtsBackendEndpoint[] | undefined;
  readonly readiness?: readonly Row[] | undefined;
  readonly diagnostics?: readonly ComponentDiagnostic[] | undefined;
  readonly loading?: boolean | undefined;
  readonly error?: string | null | undefined;
  readonly onProbeEndpoint?:
    | ((row: Row) => void | Promise<void>)
    | undefined;
  readonly onProbeModule?:
    | ((moduleName: string) => void | Promise<void>)
    | undefined;
  readonly onMarkLiveRequired?:
    | ((row: Row) => void | Promise<void>)
    | undefined;
  readonly onRefresh?: (() => void | Promise<void>) | undefined;
};

const readinessColumns: readonly CmtsGridColumn<Row>[] = [
  {
    id: "module",
    header: "Module",
    accessor: readModule,
    sortable: true,
    filterable: true,
    minWidth: 150,
  },
  {
    id: "httpMethod",
    header: "Method",
    accessor: readMethod,
    sortable: true,
    filterable: true,
    minWidth: 100,
  },
  {
    id: "endpoint",
    header: "Endpoint",
    accessor: readEndpoint,
    sortable: true,
    filterable: true,
    minWidth: 310,
  },
  {
    id: "readinessStatus",
    header: "Readiness",
    accessor: readReadiness,
    sortable: true,
    filterable: true,
    minWidth: 175,
  },
  {
    id: "expectedPermission",
    header: "Permission",
    accessor: readPermission,
    sortable: true,
    filterable: true,
    minWidth: 190,
  },
  {
    id: "repositoryMethod",
    header: "Repository",
    accessor: readRepository,
    sortable: true,
    filterable: true,
    minWidth: 210,
  },
  {
    id: "offlineFallbackReady",
    header: "Offline",
    accessor: readOfflineStatus,
    sortable: true,
    filterable: true,
    minWidth: 140,
  },
  {
    id: "failureReason",
    header: "Failure / Residual",
    accessor: readFailureReason,
    sortable: true,
    filterable: true,
    minWidth: 280,
  },
];

export function CmtsBackendEndpointCoveragePage(
  props: CmtsBackendEndpointCoveragePageProps,
) {
  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("ALL");
  const [methodFilter, setMethodFilter] = useState("ALL");
  const [readinessFilter, setReadinessFilter] =
    useState("ALL");
  const [offlineFilter, setOfflineFilter] = useState("ALL");

  const [actionRow, setActionRow] = useState<Row | null>(
    null,
  );
  const [detailRow, setDetailRow] = useState<Row | null>(
    null,
  );
  const [detailMode, setDetailMode] =
    useState<DetailMode>("schema");
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] =
    useState<ExportFormat>("CSV");
  const [busyAction, setBusyAction] = useState<
    string | null
  >(null);
  const [probeResult, setProbeResult] =
    useState<ProbeResult | null>(null);

  const allRows = useMemo(
    () =>
      buildEndpointRows(
        props.endpoints ?? [],
        props.readiness ?? [],
      ),
    [props.endpoints, props.readiness],
  );

  const moduleOptions = useMemo(
    () =>
      uniqueValues(allRows.map((row) => readModule(row))),
    [allRows],
  );

  const methodOptions = useMemo(
    () =>
      uniqueValues(allRows.map((row) => readMethod(row))),
    [allRows],
  );

  const readinessOptions = useMemo(
    () =>
      uniqueValues(
        allRows.map((row) => readRawReadiness(row)),
      ),
    [allRows],
  );

  const visibleRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return allRows.filter((row) => {
      const searchableText = [
        readModule(row),
        readMethod(row),
        readEndpoint(row),
        readRawReadiness(row),
        readPermission(row),
        readRepository(row),
        readOfflineStatus(row),
        readFailureReason(row),
      ]
        .join(" ")
        .toLowerCase();

      if (
        normalizedSearch &&
        !searchableText.includes(normalizedSearch)
      ) {
        return false;
      }

      if (
        moduleFilter !== "ALL" &&
        readModule(row) !== moduleFilter
      ) {
        return false;
      }

      if (
        methodFilter !== "ALL" &&
        readMethod(row) !== methodFilter
      ) {
        return false;
      }

      if (
        readinessFilter !== "ALL" &&
        readRawReadiness(row) !== readinessFilter
      ) {
        return false;
      }

      if (offlineFilter === "READY" && !isOfflineReady(row)) {
        return false;
      }

      if (
        offlineFilter === "NOT_READY" &&
        isOfflineReady(row)
      ) {
        return false;
      }

      return true;
    });
  }, [
    allRows,
    search,
    moduleFilter,
    methodFilter,
    readinessFilter,
    offlineFilter,
  ]);

  const liveVerifiedCount = allRows.filter(
    (row) => readRawReadiness(row) === "LIVE_VERIFIED",
  ).length;

  const readyCount = allRows.filter((row) =>
    ["READY", "CONTRACT_READY"].includes(
      readRawReadiness(row),
    ),
  ).length;

  const offlineReadyCount = allRows.filter((row) =>
    isOfflineReady(row),
  ).length;

  const liveRequiredCount = allRows.filter(
    (row) => readRawReadiness(row) === "LIVE_REQUIRED",
  ).length;

  const actions: readonly CmtsGridRowAction<Row>[] = [
    {
      id: "endpoint-actions",
      label: "⋮ Actions",
      onClick: (row) => {
        setActionRow(row);
      },
    },
  ];

  async function runEndpointProbe(row: Row) {
    if (!props.onProbeEndpoint) {
      return;
    }

    setActionRow(null);
    setBusyAction("probe");

    try {
      await props.onProbeEndpoint(row);

      setProbeResult({
        success: true,
        title: "Endpoint probe completed",
        message: `${readMethod(row)} ${readEndpoint(
          row,
        )} was probed successfully. Readiness data has been refreshed.`,
      });
    } catch (error) {
      setProbeResult({
        success: false,
        title: "Endpoint probe failed",
        message: getErrorMessage(error),
      });
    } finally {
      setBusyAction(null);
    }
  }

  async function runComplaintProbe() {
    if (!props.onProbeModule) {
      return;
    }

    setBusyAction("module-probe");

    try {
      await props.onProbeModule("Complaint");

      setProbeResult({
        success: true,
        title: "Complaint module probe completed",
        message:
          "Complaint module endpoints were probed successfully. The readiness registry has been refreshed.",
      });
    } catch (error) {
      setProbeResult({
        success: false,
        title: "Complaint module probe failed",
        message: getErrorMessage(error),
      });
    } finally {
      setBusyAction(null);
    }
  }

  async function markLiveRequired(row: Row) {
    if (!props.onMarkLiveRequired) {
      return;
    }

    setActionRow(null);
    setBusyAction("mark-live");

    try {
      await props.onMarkLiveRequired(row);

      setProbeResult({
        success: true,
        title: "Readiness updated",
        message: `${readMethod(row)} ${readEndpoint(
          row,
        )} has been marked as Live Required.`,
      });
    } catch (error) {
      setProbeResult({
        success: false,
        title: "Readiness update failed",
        message: getErrorMessage(error),
      });
    } finally {
      setBusyAction(null);
    }
  }

  function openSchema(row: Row) {
    setActionRow(null);
    setDetailMode("schema");
    setDetailRow(row);
  }

  function openFallback(row: Row) {
    setActionRow(null);
    setDetailMode("fallback");
    setDetailRow(row);
  }

  function clearFilters() {
    setSearch("");
    setModuleFilter("ALL");
    setMethodFilter("ALL");
    setReadinessFilter("ALL");
    setOfflineFilter("ALL");
  }

  return (
    <div className="cmts-endpoint-blue">
      <PageContainer
        title="Backend Endpoint Readiness"
        subtitle="Endpoint registry, live proof status, repository mapping and offline fallback coverage."
      >
        {props.diagnostics?.length ? (
          <RuntimeDiagnosticBanner
            diagnostics={props.diagnostics}
            title="Endpoint readiness diagnostics"
          />
        ) : null}

        <section className="cmts-endpoint-hero">
          <div className="cmts-endpoint-hero__content">
            <span className="cmts-endpoint-eyebrow">
              BACKEND OPERATIONS
            </span>

            <h2>Endpoint readiness center</h2>

            <p>
              Review live proof, API contract mapping,
              permissions, repository coverage and offline
              readiness from one operational workspace.
            </p>
          </div>

          <div className="cmts-endpoint-hero__actions">
            <button
              type="button"
              className="cmts-endpoint-primary-button"
              disabled={
                !props.onProbeModule ||
                busyAction === "module-probe"
              }
              onClick={() => void runComplaintProbe()}
            >
              <span aria-hidden="true">▶</span>

              {busyAction === "module-probe"
                ? "Running probe..."
                : "Run Complaint probe"}
            </button>

            <button
              type="button"
              className="cmts-endpoint-secondary-button"
              disabled={!props.onRefresh || Boolean(props.loading)}
              onClick={() => void props.onRefresh?.()}
            >
              ↻ Refresh
            </button>
          </div>
        </section>

        <section className="cmts-endpoint-summary-grid">
          <EndpointSummaryCard
            label="Total endpoints"
            value={allRows.length}
            description="Registered backend routes"
            tone="blue"
            icon="⌘"
          />

          <EndpointSummaryCard
            label="Live verified"
            value={liveVerifiedCount}
            description="Real backend proof completed"
            tone="green"
            icon="✓"
          />

          <EndpointSummaryCard
            label="Ready"
            value={readyCount}
            description="Contract and route ready"
            tone="purple"
            icon="◆"
          />

          <EndpointSummaryCard
            label="Offline ready"
            value={offlineReadyCount}
            description="Offline fallback available"
            tone="cyan"
            icon="⇄"
          />

          <EndpointSummaryCard
            label="Live required"
            value={liveRequiredCount}
            description="Backend proof still required"
            tone="orange"
            icon="!"
          />
        </section>

        <section className="cmts-endpoint-policy">
          <div className="cmts-endpoint-policy__icon">
            ◉
          </div>

          <div className="cmts-endpoint-policy__content">
            <span>LIVE PROOF POLICY</span>
            <h3>Honest backend verification</h3>
            <p>
              LIVE_VERIFIED is assigned only after a real backend
              probe. Offline packages may report OFFLINE_READY,
              CONTRACT_READY or LIVE_REQUIRED, but never false live
              proof.
            </p>
          </div>

          <div className="cmts-endpoint-policy__status">
            <strong>{liveVerifiedCount}</strong>
            <span>LIVE_VERIFIED</span>
          </div>
        </section>

        <section className="cmts-endpoint-registry">
          <header className="cmts-endpoint-registry__header">
            <div>
              <h3>Endpoint readiness registry</h3>
              <p>
                Showing {visibleRows.length} of {allRows.length}{" "}
                endpoint records.
              </p>
            </div>

            <div className="cmts-endpoint-registry__header-actions">
              <button
                type="button"
                className="cmts-endpoint-secondary-button"
                disabled={!props.onRefresh || Boolean(props.loading)}
                onClick={() => void props.onRefresh?.()}
              >
                ↻ Refresh
              </button>

              <button
                type="button"
                className="cmts-endpoint-primary-button"
                disabled={visibleRows.length === 0}
                onClick={() => setExportOpen(true)}
              >
                ⇩ Export
              </button>
            </div>
          </header>

          <div className="cmts-endpoint-filters">
            <label className="cmts-endpoint-filter cmts-endpoint-filter--search">
              <span>Search</span>

              <input
                type="search"
                value={search}
                placeholder="Search module, endpoint, permission..."
                onChange={(event) =>
                  setSearch(event.target.value)
                }
              />
            </label>

            <label className="cmts-endpoint-filter">
              <span>Module</span>

              <select
                value={moduleFilter}
                onChange={(event) =>
                  setModuleFilter(event.target.value)
                }
              >
                <option value="ALL">All modules</option>

                {moduleOptions.map((moduleName) => (
                  <option key={moduleName} value={moduleName}>
                    {moduleName}
                  </option>
                ))}
              </select>
            </label>

            <label className="cmts-endpoint-filter">
              <span>Method</span>

              <select
                value={methodFilter}
                onChange={(event) =>
                  setMethodFilter(event.target.value)
                }
              >
                <option value="ALL">All methods</option>

                {methodOptions.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </select>
            </label>

            <label className="cmts-endpoint-filter">
              <span>Readiness</span>

              <select
                value={readinessFilter}
                onChange={(event) =>
                  setReadinessFilter(event.target.value)
                }
              >
                <option value="ALL">All readiness</option>

                {readinessOptions.map((status) => (
                  <option key={status} value={status}>
                    {humanize(status)}
                  </option>
                ))}
              </select>
            </label>

            <label className="cmts-endpoint-filter">
              <span>Offline</span>

              <select
                value={offlineFilter}
                onChange={(event) =>
                  setOfflineFilter(event.target.value)
                }
              >
                <option value="ALL">All offline states</option>
                <option value="READY">Offline ready</option>
                <option value="NOT_READY">
                  Not offline ready
                </option>
              </select>
            </label>

            <button
              type="button"
              className="cmts-endpoint-clear-button"
              onClick={clearFilters}
            >
              Clear
            </button>
          </div>

          {props.error ? (
            <div className="cmts-endpoint-error">
              <strong>Unable to load endpoint readiness</strong>
              <span>{props.error}</span>
            </div>
          ) : null}

          <div className="cmts-endpoint-grid">
            <CmtsSmartDataGrid<Row>
              title={`${visibleRows.length} endpoint ${
                visibleRows.length === 1 ? "record" : "records"
              }`}
              rows={visibleRows}
              columns={readinessColumns}
              rowKey="id"
              defaultPageSize={10}
              pageSizeOptions={[10, 25, 50, 100]}
              sortable
              loading={props.loading}
              error={props.error}
              actions={actions}
              onRowClick={openSchema}
              emptyMessage="No backend endpoint readiness records match the selected filters."
            />
          </div>
        </section>
      </PageContainer>

      {actionRow ? (
        <EndpointActionsModal
          row={actionRow}
          canProbe={Boolean(props.onProbeEndpoint)}
          canMarkLive={Boolean(props.onMarkLiveRequired)}
          busy={Boolean(busyAction)}
          onClose={() => setActionRow(null)}
          onProbe={() => void runEndpointProbe(actionRow)}
          onSchema={() => openSchema(actionRow)}
          onFallback={() => openFallback(actionRow)}
          onMarkLive={() => void markLiveRequired(actionRow)}
        />
      ) : null}

      {detailRow ? (
        <EndpointDetailModal
          row={detailRow}
          mode={detailMode}
          onClose={() => setDetailRow(null)}
        />
      ) : null}

      {probeResult ? (
        <ProbeResultModal
          result={probeResult}
          onClose={() => setProbeResult(null)}
        />
      ) : null}

      {exportOpen ? (
        <EndpointExportModal
          recordCount={visibleRows.length}
          format={exportFormat}
          onFormatChange={setExportFormat}
          onClose={() => setExportOpen(false)}
          onExport={() => {
            downloadEndpointRows(
              visibleRows,
              exportFormat,
            );
            setExportOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function EndpointSummaryCard(props: {
  readonly label: string;
  readonly value: number;
  readonly description: string;
  readonly tone: "blue" | "green" | "purple" | "cyan" | "orange";
  readonly icon: string;
}) {
  return (
    <article
      className={`cmts-endpoint-summary-card cmts-endpoint-summary-card--${props.tone}`}
    >
      <div className="cmts-endpoint-summary-card__top">
        <span>{props.label}</span>
        <i>{props.icon}</i>
      </div>

      <strong>{props.value}</strong>
      <p>{props.description}</p>
    </article>
  );
}

function EndpointActionsModal(props: {
  readonly row: Row;
  readonly canProbe: boolean;
  readonly canMarkLive: boolean;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onProbe: () => void;
  readonly onSchema: () => void;
  readonly onFallback: () => void;
  readonly onMarkLive: () => void;
}) {
  return (
    <ModalShell
      title="Endpoint actions"
      subtitle={`${readMethod(props.row)} ${readEndpoint(
        props.row,
      )}`}
      onClose={props.onClose}
    >
      <div className="cmts-audit-action-list">
        <ActionOption
          icon="▶"
          title="Run endpoint probe"
          description="Execute a live backend verification for this endpoint."
          disabled={!props.canProbe || props.busy}
          onClick={props.onProbe}
        />

        <ActionOption
          icon="⌘"
          title="View endpoint schema"
          description="Review route, permission, repository and contract details."
          onClick={props.onSchema}
        />

        <ActionOption
          icon="⇄"
          title="View offline fallback"
          description="Review offline readiness and fallback configuration."
          onClick={props.onFallback}
        />

        <ActionOption
          icon="!"
          title="Mark live required"
          description="Mark this endpoint as requiring real backend proof."
          disabled={!props.canMarkLive || props.busy}
          onClick={props.onMarkLive}
        />
      </div>
    </ModalShell>
  );
}

function ActionOption(props: {
  readonly icon: string;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="cmts-audit-action-option"
      disabled={props.disabled}
      onClick={props.onClick}
    >
      <span className="cmts-audit-action-option__icon">
        {props.icon}
      </span>

      <span>
        <strong>{props.title}</strong>
        <small>{props.description}</small>
      </span>

      <span className="cmts-audit-action-option__arrow">
        ›
      </span>
    </button>
  );
}

function EndpointDetailModal(props: {
  readonly row: Row;
  readonly mode: DetailMode;
  readonly onClose: () => void;
}) {
  const schemaItems = buildSchemaItems(props.row);
  const fallbackItems = buildFallbackItems(props.row);
  const items =
    props.mode === "schema" ? schemaItems : fallbackItems;

  return (
    <ModalShell
      title={
        props.mode === "schema"
          ? "Endpoint schema"
          : "Offline fallback"
      }
      subtitle={`${readMethod(props.row)} ${readEndpoint(
        props.row,
      )}`}
      onClose={props.onClose}
      wide
    >
      <div className="cmts-endpoint-detail-banner">
        <div>
          <span>{readModule(props.row)}</span>
          <strong>{readEndpoint(props.row)}</strong>
        </div>

        <span className="cmts-endpoint-detail-status">
          {readReadiness(props.row)}
        </span>
      </div>

      <div className="cmts-endpoint-detail-grid">
        {items.map((item) => (
          <div
            key={item.label}
            className="cmts-endpoint-detail-item"
          >
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onClose}
        >
          Done
        </button>
      </div>
    </ModalShell>
  );
}

function ProbeResultModal(props: {
  readonly result: ProbeResult;
  readonly onClose: () => void;
}) {
  return (
    <ModalShell
      title={props.result.title}
      onClose={props.onClose}
    >
      <div
        className={`cmts-endpoint-probe-result ${
          props.result.success
            ? "cmts-endpoint-probe-result--success"
            : "cmts-endpoint-probe-result--failure"
        }`}
      >
        <span>{props.result.success ? "✓" : "!"}</span>

        <div>
          <strong>
            {props.result.success
              ? "Probe successful"
              : "Probe failed"}
          </strong>

          <p>{props.result.message}</p>
        </div>
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onClose}
        >
          Close
        </button>
      </div>
    </ModalShell>
  );
}

function EndpointExportModal(props: {
  readonly recordCount: number;
  readonly format: ExportFormat;
  readonly onFormatChange: (format: ExportFormat) => void;
  readonly onClose: () => void;
  readonly onExport: () => void;
}) {
  const formats: readonly {
    format: ExportFormat;
    title: string;
    description: string;
  }[] = [
    {
      format: "CSV",
      title: "CSV spreadsheet",
      description: "Best for data analysis and imports.",
    },
    {
      format: "XLS",
      title: "Excel workbook",
      description: "Formatted spreadsheet for reporting.",
    },
    {
      format: "JSON",
      title: "JSON data",
      description: "Structured data for integrations.",
    },
  ];

  return (
    <ModalShell
      title="Export endpoint readiness"
      subtitle="Download the currently filtered endpoint records."
      onClose={props.onClose}
      wide
    >
      <div className="cmts-export-banner">
        <span className="cmts-export-banner__icon">⇩</span>

        <div>
          <small>DATA EXPORT</small>
          <strong>Prepare your export</strong>
          <p>
            Export readable endpoint, permission, repository and
            readiness values.
          </p>
        </div>

        <span className="cmts-export-record-count">
          {props.recordCount} records
        </span>
      </div>

      <section className="cmts-export-format-section">
        <div className="cmts-export-format-heading">
          <span>01</span>

          <div>
            <strong>Select file format</strong>
            <small>
              Choose the required download format.
            </small>
          </div>

          <em>Required</em>
        </div>

        <div className="cmts-export-format-grid">
          {formats.map((item) => {
            const selected = item.format === props.format;

            return (
              <button
                type="button"
                key={item.format}
                className={`cmts-export-format-card${
                  selected
                    ? " cmts-export-format-card--selected"
                    : ""
                }`}
                onClick={() =>
                  props.onFormatChange(item.format)
                }
              >
                <span className="cmts-export-format-badge">
                  {item.format}
                </span>

                <span
                  className={`cmts-export-radio${
                    selected
                      ? " cmts-export-radio--selected"
                      : ""
                  }`}
                >
                  {selected ? "✓" : ""}
                </span>

                <strong>{item.title}</strong>
                <small>{item.description}</small>
              </button>
            );
          })}
        </div>
      </section>

      <div className="cmts-export-info">
        <span>i</span>

        <p>
          <strong>Filtered view will be exported</strong>
          Search and selected filter values are applied to the
          downloaded file.
        </p>
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-secondary-button"
          onClick={props.onClose}
        >
          Cancel
        </button>

        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onExport}
        >
          Download {props.format}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell(props: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
  readonly onClose: () => void;
  readonly wide?: boolean;
}) {
  return (
    <div
      className="cmts-audit-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          props.onClose();
        }
      }}
    >
      <section
        className={`cmts-audit-modal${
          props.wide ? " cmts-audit-modal--wide" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
      >
        <header className="cmts-audit-modal__header">
          <div>
            <h3>{props.title}</h3>

            {props.subtitle ? (
              <p>{props.subtitle}</p>
            ) : null}
          </div>

          <button
            type="button"
            className="cmts-audit-modal__close"
            aria-label="Close"
            onClick={props.onClose}
          >
            ×
          </button>
        </header>

        <div className="cmts-audit-modal__body">
          {props.children}
        </div>
      </section>
    </div>
  );
}

function buildEndpointRows(
  endpoints: readonly CmtsBackendEndpoint[],
  readiness: readonly Row[],
): readonly Row[] {
  const inventoryRows = endpoints.map(
    (endpoint) => endpoint as unknown as Row,
  );

  const inventoryByRoute = new Map<string, Row>();
  const matchedInventory = new Set<Row>();

  for (const inventoryRow of inventoryRows) {
    inventoryByRoute.set(
      endpointMatchKey(inventoryRow),
      inventoryRow,
    );
  }

  const mergedRows = readiness.map((readinessRow) => {
    const inventoryRow = inventoryByRoute.get(
      endpointMatchKey(readinessRow),
    );

    if (inventoryRow) {
      matchedInventory.add(inventoryRow);
    }

    return normalizeEndpointRow({
      ...(inventoryRow ?? {}),
      ...readinessRow,
    });
  });

  for (const inventoryRow of inventoryRows) {
    if (!matchedInventory.has(inventoryRow)) {
      mergedRows.push(normalizeEndpointRow(inventoryRow));
    }
  }

  return mergedRows.map((row, index) => ({
    ...row,
    id:
      row.id ??
      row.endpointId ??
      row.code ??
      `${readMethod(row)}-${readEndpoint(row)}-${index}`,
  }));
}

function normalizeEndpointRow(row: Row): Row {
  return {
    ...row,
    module:
      row.moduleName ??
      row.moduleCode ??
      row.module ??
      row.feature,
    httpMethod:
      row.httpMethod ??
      row.method ??
      row.verb,
    endpoint:
      row.route ??
      row.endpoint ??
      row.path ??
      row.url,
    readinessStatus:
      row.statusName ??
      row.status ??
      row.readinessStatus ??
      row.proofStatus,
    expectedPermission:
      row.requiredPermissionName ??
      row.requiredPermissionCode ??
      row.expectedPermission ??
      row.permissionCode,
    repositoryMethod:
      row.repositoryDisplayName ??
      row.repositoryMethod ??
      row.repository ??
      row.clientMethod,
    offlineFallbackReady:
      row.offlineFallbackReady ??
      row.isRequiredForOffline ??
      row.offlineReady ??
      row.hasOfflineFallback,
    failureReason:
      row.lastProbeError ??
      row.failureReason ??
      row.residualRisk ??
      row.errorMessage,
  };
}

function endpointMatchKey(row: Row): string {
  const endpoint = String(
    row.route ?? row.endpoint ?? row.path ?? row.url ?? "",
  )
    .trim()
    .toLowerCase();

  const method = String(
    row.httpMethod ?? row.method ?? row.verb ?? "",
  )
    .trim()
    .toUpperCase();

  return `${method}:${endpoint}`;
}

function readModule(row: Row): string {
  return humanize(
    row.moduleName ??
      row.moduleCode ??
      row.module ??
      row.feature,
    "Core",
  );
}

function readMethod(row: Row): string {
  return String(
    row.httpMethod ?? row.method ?? row.verb ?? "GET",
  )
    .trim()
    .toUpperCase();
}

function readEndpoint(row: Row): string {
  return String(
    row.route ??
      row.endpoint ??
      row.path ??
      row.url ??
      "Route not registered",
  ).trim();
}

function readRawReadiness(row: Row): string {
  return String(
    row.statusName ??
      row.status ??
      row.readinessStatus ??
      row.proofStatus ??
      "NOT_VERIFIED",
  )
    .trim()
    .toUpperCase();
}

function readReadiness(row: Row): string {
  const status = readRawReadiness(row);

  if (status === "LIVE_VERIFIED") {
    return "🟢 Live Verified";
  }

  if (
    status === "READY" ||
    status === "CONTRACT_READY"
  ) {
    return `🔵 ${humanize(status)}`;
  }

  if (status === "OFFLINE_READY") {
    return "🟣 Offline Ready";
  }

  if (status === "LIVE_REQUIRED") {
    return "🟠 Live Required";
  }

  if (
    status.includes("FAIL") ||
    status.includes("BLOCK")
  ) {
    return `🔴 ${humanize(status)}`;
  }

  return `⚪ ${humanize(status)}`;
}

function readPermission(row: Row): string {
  const value = String(
    row.requiredPermissionName ??
      row.requiredPermissionCode ??
      row.expectedPermission ??
      row.permissionCode ??
      "",
  ).trim();

  if (!value || isUuid(value)) {
    return "No permission required";
  }

  return value;
}

function readRepository(row: Row): string {
  const value = String(
    row.repositoryDisplayName ??
      row.repositoryMethod ??
      row.repository ??
      row.clientMethod ??
      "",
  ).trim();

  if (!value || isUuid(value)) {
    return "Not mapped";
  }

  return value;
}

function readOfflineStatus(row: Row): string {
  return isOfflineReady(row)
    ? "🟢 Offline Ready"
    : "⚪ Not Required";
}

function isOfflineReady(row: Row): boolean {
  const value =
    row.offlineFallbackReady ??
    row.isRequiredForOffline ??
    row.offlineReady ??
    row.hasOfflineFallback;

  if (typeof value === "boolean") {
    return value;
  }

  return ["true", "yes", "ready", "1"].includes(
    String(value ?? "").trim().toLowerCase(),
  );
}

function readFailureReason(row: Row): string {
  const value = String(
    row.lastProbeError ??
      row.failureReason ??
      row.residualRisk ??
      row.errorMessage ??
      "",
  ).trim();

  return value || "No failure recorded";
}

function buildSchemaItems(
  row: Row,
): readonly { label: string; value: string }[] {
  return [
    { label: "Module", value: readModule(row) },
    { label: "HTTP Method", value: readMethod(row) },
    { label: "Endpoint", value: readEndpoint(row) },
    { label: "Readiness", value: readReadiness(row) },
    { label: "Permission", value: readPermission(row) },
    { label: "Repository", value: readRepository(row) },
    {
      label: "Request Contract",
      value: formatStructuredValue(
        row.requestSchema ??
          row.requestContract ??
          row.requestType,
      ),
    },
    {
      label: "Response Contract",
      value: formatStructuredValue(
        row.responseSchema ??
          row.responseContract ??
          row.responseType,
      ),
    },
  ];
}

function buildFallbackItems(
  row: Row,
): readonly { label: string; value: string }[] {
  return [
    {
      label: "Offline Status",
      value: readOfflineStatus(row),
    },
    {
      label: "Fallback Strategy",
      value: formatStructuredValue(
        row.offlineFallback ??
          row.offlineStrategy ??
          row.fallbackType,
      ),
    },
    {
      label: "Offline Entity",
      value: formatStructuredValue(
        row.offlineEntity ??
          row.localEntity ??
          row.cacheEntity,
      ),
    },
    {
      label: "Cache Policy",
      value: formatStructuredValue(
        row.cachePolicy ?? row.offlineCachePolicy,
      ),
    },
    {
      label: "Repository",
      value: readRepository(row),
    },
    {
      label: "Failure / Residual",
      value: readFailureReason(row),
    },
  ];
}

function formatStructuredValue(value: unknown): string {
  if (value === undefined || value === null || value === "") {
    return "Not configured";
  }

  if (Array.isArray(value)) {
    return value.length
      ? value
          .map((item) => formatStructuredValue(item))
          .join(", ")
      : "Not configured";
  }

  if (typeof value === "object") {
    const entries = Object.entries(
      value as Record<string, unknown>,
    );

    if (!entries.length) {
      return "Not configured";
    }

    return entries
      .map(
        ([key, item]) =>
          `${humanize(key)}: ${formatStructuredValue(item)}`,
      )
      .join(" • ");
  }

  return String(value);
}

function humanize(
  value: unknown,
  fallback = "Not available",
): string {
  const text = String(value ?? "").trim();

  if (!text || isUuid(text)) {
    return fallback;
  }

  return text
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (character) =>
      character.toUpperCase(),
    );
}

function uniqueValues(
  values: readonly string[],
): readonly string[] {
  return [...new Set(values.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "The backend operation could not be completed.";
}

function downloadEndpointRows(
  rows: readonly Row[],
  format: ExportFormat,
) {
  const exportRows = rows.map((row) => ({
    Module: readModule(row),
    Method: readMethod(row),
    Endpoint: readEndpoint(row),
    Readiness: readReadiness(row).replace(/^[^\s]+\s/, ""),
    Permission: readPermission(row),
    Repository: readRepository(row),
    Offline: readOfflineStatus(row).replace(/^[^\s]+\s/, ""),
    "Failure / Residual": readFailureReason(row),
  }));

  const timestamp = new Date()
    .toISOString()
    .slice(0, 16)
    .replace(/[:T]/g, "-");

  if (format === "JSON") {
    saveBlob(
      JSON.stringify(exportRows, null, 2),
      `endpoint-readiness-${timestamp}.json`,
      "application/json;charset=utf-8",
    );
    return;
  }

  const headers = Object.keys(exportRows[0] ?? {});

  if (format === "XLS") {
    const html = `
      <html>
        <head><meta charset="UTF-8" /></head>
        <body>
          <table border="1">
            <thead>
              <tr>
                ${headers
                  .map(
                    (header) =>
                      `<th>${escapeHtml(header)}</th>`,
                  )
                  .join("")}
              </tr>
            </thead>
            <tbody>
              ${exportRows
                .map(
                  (row) => `
                    <tr>
                      ${headers
                        .map(
                          (header) =>
                            `<td>${escapeHtml(
                              String(
                                row[
                                  header as keyof typeof row
                                ] ?? "",
                              ),
                            )}</td>`,
                        )
                        .join("")}
                    </tr>
                  `,
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    saveBlob(
      html,
      `endpoint-readiness-${timestamp}.xls`,
      "application/vnd.ms-excel;charset=utf-8",
    );
    return;
  }

  const csvRows = [
    headers.map(csvCell).join(","),
    ...exportRows.map((row) =>
      headers
        .map((header) =>
          csvCell(
            row[header as keyof typeof row] ?? "",
          ),
        )
        .join(","),
    ),
  ];

  saveBlob(
    `\uFEFF${csvRows.join("\n")}`,
    `endpoint-readiness-${timestamp}.csv`,
    "text/csv;charset=utf-8",
  );
}

function csvCell(value: unknown): string {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function saveBlob(
  content: string,
  fileName: string,
  mimeType: string,
) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = fileName;

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(url);
}